import { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Clock, FileText, ExternalLink } from "lucide-react";
import portalApi from "@/lib/portalApi";
import { useT } from "@/i18n";

export default function PortalPayments() {
  const t = useT();
  const p = t.portal;
  const [payments, setPayments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      portalApi.get("/portal/payments").then((r) => setPayments(r.data)),
      portalApi.get("/portal/invoices").then((r) => setInvoices(r.data)),
    ]).finally(() => setLoading(false));
  }, []);

  const today = new Date().toISOString().slice(0, 10);
  const paidList = payments.filter((x) => x.status === "paid");
  const pendingList = payments.filter((x) => x.status === "pending");
  const totalPaid = paidList.reduce((s, x) => s + x.amount, 0);
  const totalPending = pendingList.reduce((s, x) => s + x.amount, 0);
  const openInvoices = invoices.filter((i) => i.status !== "paid");

  if (loading) return <div className="space-y-4">{[...Array(3)].map((_, i) => <div key={i} className="h-20 crm-card animate-pulse" />)}</div>;

  return (
    <div data-testid="portal-payments">
      <h1 className="crm-heading mb-8">{p.paymentsTitle}</h1>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="crm-card p-5">
          <div className="flex items-center gap-2 mb-2"><CheckCircle2 className="w-4 h-4" style={{ color: "#10b981" }} /><span className="text-xs font-semibold uppercase" style={{ color: "var(--crm-text-muted)" }}>{p.totalPaid}</span></div>
          <p className="text-2xl font-bold" style={{ color: "#10b981" }}>¥{totalPaid.toLocaleString()}</p>
        </div>
        <div className="crm-card p-5">
          <div className="flex items-center gap-2 mb-2"><Clock className="w-4 h-4" style={{ color: "#f97316" }} /><span className="text-xs font-semibold uppercase" style={{ color: "var(--crm-text-muted)" }}>{p.pendingAmount}</span></div>
          <p className="text-2xl font-bold" style={{ color: "#f97316" }}>¥{totalPending.toLocaleString()}</p>
        </div>
        <div className="crm-card p-5">
          <div className="flex items-center gap-2 mb-2"><FileText className="w-4 h-4" style={{ color: "#3b82f6" }} /><span className="text-xs font-semibold uppercase" style={{ color: "var(--crm-text-muted)" }}>{p.openInvoices}</span></div>
          <p className="text-2xl font-bold" style={{ color: "var(--crm-text)" }}>{openInvoices.length}</p>
        </div>
      </div>

      {invoices.length > 0 && (
        <div className="mb-8">
          <h2 className="text-base font-bold mb-4" style={{ color: "var(--crm-text)" }}>{p.invoices}</h2>
          <div className="space-y-3">
            {invoices.map((inv) => (
              <div key={inv.id} className="crm-card p-4 flex items-center justify-between"
                style={{ borderLeft: `3px solid ${inv.status === "paid" ? "#10b981" : "#f97316"}` }}>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{ background: inv.status === "paid" ? "rgba(16,185,129,0.1)" : "rgba(249,115,22,0.1)", color: inv.status === "paid" ? "#10b981" : "#f97316" }}>
                    <FileText className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-base font-bold" style={{ color: "var(--crm-text)" }}>¥{inv.amount?.toLocaleString()}</p>
                    <p className="text-xs" style={{ color: "var(--crm-text-muted)" }}>{inv.due_date ? `${p.dueDateLabel}: ${inv.due_date}` : inv.created}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-bold"
                    style={{ background: inv.status === "paid" ? "rgba(16,185,129,0.12)" : "rgba(249,115,22,0.12)", color: inv.status === "paid" ? "#10b981" : "#f97316" }}>
                    {inv.status === "paid" ? p.paid : p.open}
                  </span>
                  {inv.status !== "paid" && inv.hosted_invoice_url && (
                    <a href={inv.hosted_invoice_url} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-sm font-bold px-5 py-2.5 rounded-xl"
                      style={{ color: "white", background: "var(--crm-accent)", boxShadow: "0 2px 10px rgba(249,115,22,0.3)" }}>
                      <ExternalLink className="w-4 h-4" /> {p.payNow}
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <h2 className="text-base font-bold mb-4" style={{ color: "var(--crm-text)" }}>{p.history}</h2>
      <div className="crm-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="crm-table">
            <thead><tr><th>{p.amount}</th><th>{p.dueDateLabel}</th><th>{p.paymentDate}</th><th>{p.status}</th></tr></thead>
            <tbody>
              {payments.length === 0 ? (
                <tr><td colSpan={4} className="text-center py-14" style={{ color: "var(--crm-text-dim)" }}>{p.noPayment}</td></tr>
              ) : payments.map((x) => (
                <tr key={x.id} style={{ background: x.status === "pending" && x.due_date < today ? "rgba(239,68,68,0.04)" : undefined }}>
                  <td style={{ fontWeight: 700, fontSize: "16px", color: "var(--crm-text)" }}>¥{x.amount.toLocaleString()}</td>
                  <td style={{ color: x.status === "pending" && x.due_date < today ? "#ef4444" : "var(--crm-text-secondary)" }}>{x.due_date}</td>
                  <td style={{ color: x.payment_date ? "#10b981" : "var(--crm-text-dim)" }}>{x.payment_date || "—"}</td>
                  <td>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold"
                      style={{ background: x.status === "paid" ? "rgba(16,185,129,0.12)" : x.status === "pending" && x.due_date < today ? "rgba(239,68,68,0.15)" : "rgba(249,115,22,0.12)",
                        color: x.status === "paid" ? "#10b981" : x.status === "pending" && x.due_date < today ? "#ef4444" : "#f97316" }}>
                      {x.status === "paid" ? p.paid : x.status === "pending" && x.due_date < today ? p.overdue : p.pendingLabel}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
